import {React, action, observable, classNames, Sentry, useLocalStore, _} from '@api'
import {LoadingIndicator} from '@design-board/components'
import {FrozenPdfDocumentSnackbarContent} from '@modules/pdf/FrozenPdfDocumentMessage'
import {aws, site, StoresInstance} from '@stores'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {autorun, computed, runInAction, toJS, reaction, when} from 'mobx'
import {disposeOnUnmount} from 'mobx-react'
import {observer} from 'mobx-react-lite'
import {Fragment, useEffect} from 'react'
import {useAsync, useMeasure} from 'react-use'
import * as css from './CatalogPdfExporter.css'
import {Snackbar} from '@material-ui/core'
import ReactPDF, {Document, PDFViewer, Font} from '@react-pdf/renderer'
import {observer as classObserver} from 'mobx-react'
import { DEBUG_PDF } from './PdfExportStore'

// Race condition with custom font - https://github.com/diegomura/react-pdf/issues/310
// Give the pdf code time to load all of our fonts

export class CatalogPdfViewerStore extends VipLocalStore {
  constructor(stores: StoresInstance) {
    super(stores)

    synchronizeStores(() => {
      const {workspaces, catalog, stores: {pdf}} = this

      pdf.viewStore = this

      when(
        () => workspaces.deploymentConfig != null,
        () => {
          if (pdf.isFrozen) {
            this.fontsLoaded = true
          } else {
            const {pdfConfig: {fonts}} = catalog
            if (!_.isEmpty(fonts)) {
              console.groupCollapsed(`Registering fonts for PDF render`)
              fonts.map(({path, name}) => {
                console.log(`Registering custom PDF font - ${name} - ${path}`)
                Font.register({
                  src: path.replace('/static', ''),
                  family: name
                })
              })
              console.groupEnd()
              setTimeout(() => this.fontsLoaded = true, 1000)
            } else {
              this.fontsLoaded = true
            }
            Font.register({
              src: aws.useCloudFront ? aws.fontUrlFor('MyriadPro-Regular.ttf') : '/fonts/MyriadPro-Regular.ttf',
              family: 'NotoSans'
            })
          }
        })
    })
  }

  @observable fontsLoaded = false

  registerEffects = () => {
    const toDispose = [
      autorun(async () => {
        const {pdf, stores: {flexLayoutCatalogRoot}} = this
        if (pdf) {
          const {pdf: {isFrozen, search, hasSubGroup, searchPayload}} = this
          if (!isFrozen && searchPayload && (!flexLayoutCatalogRoot || (flexLayoutCatalogRoot.isPdfView && flexLayoutCatalogRoot.visible))) {
            await search()
          }
        }
      }, {name: `Run PDF query when appropriate`})
    ]

    return () => toDispose.forEach(d => d())
  }
}

export const CatalogPdfExporterContainer = observer<{
  hierarchy?: string[]
  className?: string
}>(({className}) => {
  const store = useVipLocalStore((ws) => new CatalogPdfViewerStore(ws)),
    {
      stores, catalog,
      stores: {pdf}
    } = store

  useEffect(() => store.registerEffects(), [])

  if (!pdf) return null

  const {searchPayload, isFrozen, undo, search, query: {data, loading}} = pdf,
    skip = !searchPayload || (isFrozen ?? pdf.query.data)

  const [measurerRef, {width, height}] = useMeasure()
  // YFTS-6211 PDF needs to resize internal render after container resizes
  useEffect(() => {
    if (!pdf.isLoading && pdf.document && !pdf.isRenderingPdf && pdf.config.reRenderOnResize) {
      pdf.updateViewKey_debounce()
    }
  }, [width])

  return (
    !isFrozen && (!store.fontsLoaded || loading || _.isEmpty(data) || !searchPayload)
    ? <div className={classNames(className, css.root)}>
      <LoadingIndicator className={classNames(className, css.loading)}
                        text={loading ? 'Loading data...' : !store.fontsLoaded ? 'Loading fonts...' : _.isEmpty(data) ? `No Data` : `?????`}/>
    </div>
    : <div className={classNames(className, css.root)} ref={measurerRef}>
      <CatalogPdfExporter stores={stores} className={className}/>
    </div>)
})

interface MyProps1 {
  className?: string
  stores: StoresInstance
}

/**
 * Class component so we can have componentDidCatch
 */
@classObserver
export class CatalogPdfExporter extends React.Component<MyProps1 /*, { isCreatingPdf?, isRenderingPdf? }*/> {
  componentDidCatch(error: Error, extraInfo): void {
    console.error(error)
    Sentry.captureException(error)
  }

  get stores() { return this.props.stores}

// resizePdfToFit = autorun(() => {
//     const {stores: {pdf, sidebar: {left: {open: leftOpen, width}, right: {open}}}} = this,
//       {ref} = this,
//       {document} = pdf
//
//     if (document) {
//       // const iframe = ref?.querySelector('iframe') as HTMLIFrameElement
//       // setTimeout(() => {
//       //   const src = iframe.src
//       //   iframe.src = ''
//       //   iframe.src = src
//       //   //iframe.contentWindow.location.reload(true)
//       // }, 5)
//     }
//   }, {name: 'Resize PDF to fit when catalog view changes'})

  // @disposeOnUnmount updateViewKey = autorun(async () => {
  //   const {
  //           catalog:   {},
  //           site:      {seasonal: {season, category1, category2}},
  //           dashboard: {cardView, groupBy: {hierarchy, isProductView}},
  //           pdf,
  //           pdf:       {
  //                        config: {document: {author, title, subject}, sections: {specGlossary, cover, products}, selectedTemplate, imageSize, watermark}
  //                      }
  //         } = stores
  //
  //   trace()
  //   runInAction(`Reset view key and regenerate PDF report`, () => {
  //     this.initialPdfRender = true
  //     pdf.updateViewKey()
  //   })
  //
  // }, {
  //   name: `Regenerate PDF report due to dependent store value changing (season, hierarchy, cardView fields, etc...)`
  // })

  // region State

  // endregion

  @observable.ref ref: HTMLDivElement
  setRef = r => this.ref = r

  render() {
    const {
        props: {className}, setRef,
        stores: {
          sidebar: {right}, catalog, pdf
          //catalog: {loadingCounts}
        }
      } = this,
      {viewerKey, query, isLoading, isCreatingPdf, isRenderingPdf, document, hide, isFrozen, undo: {isDirty}} = pdf

    // trace()
    //console.log({isCreatingPdf, isLoading, isFrozen, isRenderingPdf, document, viewerKey, query})
    return <div id='YSL_PDF_Viewer'
                data-name='pdf-viewer'
                ref={setRef}
                data-view-option-dragging={catalog.viewOptionIsDragging}
                className={classNames(className, css.root, {[css.sidebar]: right.open})} data-loading={isLoading} data-frozen={isFrozen} data-dirty={isDirty}>
      <Fragment>
        {(isLoading || isRenderingPdf) &&
		 <LoadingIndicator className={css.loading} text={isCreatingPdf || isRenderingPdf ? 'Generating PDF...' : isLoading ? 'Loading PDF...' : '...'}/>}

        {!hide && !isCreatingPdf && document &&
		 <PDFViewer key={viewerKey} data-viewer-key={viewerKey} style={{height: "100%", width: '100%'}}
		            className={classNames(css.pdfViewer)}
                showToolbar={DEBUG_PDF}
		            children={document}/>}

        <Snackbar open={isFrozen && isDirty}
                  className={css.pdfSnackbar}
          //anchorOrigin={{horizontal: 'center', vertical: 'top'}}
        >
          <FrozenPdfDocumentSnackbarContent/>
        </Snackbar>
      </Fragment>
    </div>
  }

  /*

  // https://github.com/diegomura/react-pdf/issues/420#issuecomment-475981586
  // shouldComponentUpdate(nextProps: Readonly<MyProps1>, nextState: Readonly<{ isCreatingPdf }>, nextContext: any): boolean {
  //   return !nextState.isCreatingPdf
  // }

  state = {isCreatingPdf: false, isRenderingPdf: false}

   */
  // region PDF Generation
}



// WEBPACK FOOTER //
// ./src/modules/pdf/CatalogPdfExporter.tsx